import { useState, useEffect } from 'react';
import { apiInstance } from '../../../api/apiInstance';
import { DashboardData, transformDate } from '../../../pages/dashboard/util';
import { DashboardFilters } from '../../../utils/constant';
import { downloadFile } from 'src/utils/utility-functions';

interface UseTableProps {
  activityType?: 'recent' | 'history';
  filters?: DashboardFilters;
  isAdmin?: boolean;
}

const generateQueryParams = (filters?: DashboardFilters) => {
  const params = new URLSearchParams();

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          value.forEach((v) => params.append(key, v));
        } else if (key === 'searchValue') {
          params.append('searchKey', 'trackingNumber');
          params.append('searchKey', 'case_numbers');
          params.append('searchKey', 'user');
          params.append('searchKey', 'organization.name');
          params.append('searchKey', 'user.firstName');
          params.append('searchKey', 'user.lastName');
          params.append('searchValue', value);
        } else {
          params.append(key, value);
        }
      }
    });
  }
  return params;
};

const buildRequestUrl = (baseUrl: string, filters?: DashboardFilters) => {
  const params = generateQueryParams(filters);
  const requestUrl = params.size > 0 ? `${baseUrl}&${params.toString()}` : baseUrl;
  return requestUrl;
};

export const formatData = (data: any) => {
  const newData = data.map((el: any) => {
    const {
      id,
      caseNumbers,
      partiesCount,
      createdAt,
      docsCount,
      pagesCount,
      sheetsCount,
      trackingNumber,
      orderPreferences,
      debtorName,
      mailMethod,
      isCancelled,
      organization,
      status,
      user,
    } = el;
    return {
      id,
      tracking: trackingNumber,
      dateTime: transformDate(createdAt),
      debtorName,
      caseNumbers: caseNumbers?.join(', ') || '-',
      docs: docsCount,
      pages: pagesCount,
      sheets: sheetsCount,
      parties: partiesCount,
      service: orderPreferences?.service ?? 'normal',
      jobStatus: status,
      type: el.type.split(' ')[0].charAt(0).toUpperCase() + el.type.split(' ')[0].slice(1).toLowerCase(),
      organizationName: organization.name,
      user: `${user.firstName} ${user.lastName}`,
      orgType: organization.type,
    };
  });

  return newData.sort((a: any, b: any) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime());
};

const useAdminTable = ({ activityType, filters, isAdmin }: UseTableProps) => {
  const [data, setData] = useState<DashboardData[]>([]);

  const [loading, setLoading] = useState<boolean>(true);

  const [errorMsg, setErrorMsg] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);

  const [error, setError] = useState<boolean>(false);

  const exportData = async (type: 'csv' | 'invoice') => {
    setExportAnchor(null);
    const baseUrl = `/job/${type === 'csv' ? 'export' : 'export-invoices'}?activityType=${activityType}&unitType=organization`;
    const requestUrl = buildRequestUrl(baseUrl, filters);
    try {
      await downloadFile(requestUrl, `${Date.now()}_${type === 'csv' ? 'dashboard' : 'invoice'}`);
    } catch (err: any) {
      setErrorMsg(err.message);
    }
  };

  const exportItems = [
    { label: 'Export CSV', onClick: () => exportData('csv') },
    { label: 'Export Invoice', onClick: () => exportData('invoice') },
  ];

  const fetchData = async () => {
    const skip = page * rowsPerPage;
    const take = rowsPerPage;

    const baseUrl = `/job?skip=${skip}&take=${take}&activityType=${activityType}&unitType=organization`;
    const requestUrl = buildRequestUrl(baseUrl, filters);
    try {
      const response = await apiInstance.get(requestUrl);
      setData(formatData(response.data.items));
      setTotalCount(response.data.totalCount);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const updateData = (data: any) => {
    setData(data);
  };

  const openExportButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    setExportAnchor(event.currentTarget);
  };

  const closeExportButton = () => {
    setExportAnchor(null);
  };

  const clearErrorMsg = () => {
    setErrorMsg('');
  };

  useEffect(() => {
    fetchData();
  }, [filters, page, rowsPerPage]);

  return {
    data,
    error,
    loading,
    exportAnchor,
    exportItems,
    errorMsg,
    clearErrorMsg,
    openExportButton,
    setPage,
    setRowsPerPage,
    page,
    rowsPerPage,
    totalCount,
    closeExportButton,
    updateData,
  };
};

export default useAdminTable;
